import React from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import Modal from "../../../components/Modal/Modal";
import { HB_FIELD_CODE_OPTIONS } from "../../../config/products";

interface CertificateModalProps {
  isOpen: boolean;
  isEnglish: boolean;
  selectedProduct: string;
  pdfBlob: Blob | null;
  name: string;
  surname: string;
  birthDate: string;
  fieldCode: string;
  agreed: boolean;
  emailSent: boolean;
  sendingEmail: boolean;
  emailError: string | null;
  optionalEmail: string;
  userEmail?: string;
  hasUserData: boolean;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onSurnameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onFieldCodeChange: (value: string) => void;
  onAgreedChange: (value: boolean) => void;
  onSendCertificate: () => void;
}

const sanitizeName = (value: string) => value.replace(/[^A-Za-zÀ-ž\s]/g, "");

const downloadPdf = (pdfBlob: Blob | null) => {
  if (!pdfBlob) {
    return;
  }

  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "certificate.pdf";
  link.click();
  URL.revokeObjectURL(url);
};

const CertificateModal: React.FC<CertificateModalProps> = ({
  isOpen,
  isEnglish,
  selectedProduct,
  pdfBlob,
  name,
  surname,
  birthDate,
  fieldCode,
  agreed,
  emailSent,
  sendingEmail,
  emailError,
  optionalEmail,
  userEmail,
  hasUserData,
  onClose,
  onNameChange,
  onSurnameChange,
  onBirthDateChange,
  onFieldCodeChange,
  onAgreedChange,
  onSendCertificate,
}) => {
  const showFieldCodeSelector = selectedProduct === "hygienehb";
  const requiresFieldCode = selectedProduct === "hygienehb" || selectedProduct === "hygienehben";
  const isSendDisabled =
    !agreed ||
    !name.trim() ||
    !surname.trim() ||
    !birthDate ||
    (requiresFieldCode && !fieldCode) ||
    sendingEmail ||
    optionalEmail === userEmail;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="pdf-modal">
        <div className="pdf-modal__content">
          <div className="labels">
            <label>
              {isEnglish ? "*Name:" : "*Vardas:"}
              <input
                value={name}
                onChange={(event) => onNameChange(sanitizeName(event.target.value))}
                required
                disabled={hasUserData}
              />
            </label>
            <label>
              {isEnglish ? "*Last name:" : "*Pavardė:"}
              <input
                value={surname}
                onChange={(event) => onSurnameChange(sanitizeName(event.target.value))}
                required
                disabled={hasUserData}
              />
            </label>
            <label>
              {isEnglish ? "*Date of birth:" : "*Gimimo data:"}
              <input
                type="date"
                value={birthDate}
                onChange={(event) => onBirthDateChange(event.target.value)}
                disabled={hasUserData}
              />
            </label>
            {showFieldCodeSelector ? (
              <>
                <label className="custom-label">
                  *Pasirinkite veiklos srities kodą:
                  <div className="custom-select-wrapper">
                    <select
                      className="custom-select"
                      value={fieldCode}
                      onChange={(event) => onFieldCodeChange(event.target.value)}
                      required
                      disabled={hasUserData}
                    >
                      <option value="">Pasirinkite kodą</option>
                      {HB_FIELD_CODE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                <p>
                  Tinkamą veiklos srities kodą rasite{" "}
                  <a href="/faq" target="_blank" rel="noopener noreferrer" className="link-button">
                    čia
                  </a>
                  .
                </p>
              </>
            ) : null}
            <div className="checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(event) => onAgreedChange(event.target.checked)}
              />
              <p>
                {isEnglish
                  ? "*I confirm that the information shown on the certificate is correct."
                  : "*Sutinku, kad pažymėjime matomi duomenys yra teisingi."}
              </p>
            </div>
            <label>
              {userEmail ? (
                <>
                  <p>{isEnglish ? "Your email:" : "Jūsų el. paštas:"} </p>
                  <span> {userEmail}</span>
                </>
              ) : (
                <span>{isEnglish ? "Not specified" : "Nenurodytas"}</span>
              )}
            </label>
            {!emailSent && !hasUserData ? (
              <button disabled={isSendDisabled} onClick={onSendCertificate}>
                {sendingEmail
                  ? isEnglish
                    ? "Sending..."
                    : "Siunčiama..."
                  : isEnglish
                    ? "Send certificate"
                    : "Atsiųsti pažymėjimą"}
              </button>
            ) : (
              <label>
                {isEnglish
                  ? "You can also download the certificate directly to your device:"
                  : "Pažymėjimą taip pat galite atsisiųsti tiesiai į savo įrenginį:"}
                <button onClick={() => downloadPdf(pdfBlob)}>
                  {isEnglish ? "Download certificate" : "Atsisiųsti pažymėjimą"}
                </button>
              </label>
            )}
            {emailError && <p style={{ color: "red" }}>{emailError}</p>}
            <button onClick={onClose}>{isEnglish ? "Close" : "Uždaryti"}</button>
          </div>
          {pdfBlob && (
            <div className="pdf-modal__viewer">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={URL.createObjectURL(pdfBlob)} />
              </Worker>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CertificateModal;
